import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  alpha,
  useTheme,
} from '@mui/material/styles';
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  AutoStories,
  CalendarMonth,
  Close,
  Group,
  Login as LoginIcon,
  PersonAdd,
  Place,
  TaskAlt,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '../constants/app';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Logo } from '../components/Logo';
import { LoginForm } from '../components/auth/LoginForm';
import { RegisterForm } from '../components/auth/RegisterForm';

interface PublicHomePageProps {
  initialAuthMode?: 'login' | 'register';
}

interface FeatureCardDefinition {
  icon: React.ReactElement;
  title: string;
  description: string;
}

const PublicHomePage: React.FC<PublicHomePageProps> = ({ initialAuthMode }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));

  const redirectTo = searchParams.get('redirect') || '/';
  const initialEmail = searchParams.get('email') || '';
  const currentSearch = searchParams.toString();
  const search = currentSearch ? `?${currentSearch}` : '';

  const featureCards: FeatureCardDefinition[] = [
    {
      icon: <AutoStories fontSize="small" />,
      title: t('publicHome.features.guide.title'),
      description: t('publicHome.features.guide.description'),
    },
    {
      icon: <CalendarMonth fontSize="small" />,
      title: t('publicHome.features.planning.title'),
      description: t('publicHome.features.planning.description'),
    },
    {
      icon: <Group fontSize="small" />,
      title: t('publicHome.features.sharing.title'),
      description: t('publicHome.features.sharing.description'),
    },
  ];

  const openAuth = (mode: 'login' | 'register') => {
    navigate({ pathname: mode === 'login' ? '/login' : '/register', search });
  };

  const closeAuth = () => {
    navigate({ pathname: '/', search });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top left, rgba(200,107,74,0.16), transparent 24%), radial-gradient(circle at 80% 12%, rgba(35,68,54,0.18), transparent 28%), linear-gradient(180deg, #f7f1e7 0%, #f4ede1 52%, #efe5d5 100%)',
      }}
    >
      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 4 }, pb: { xs: 8, md: 10 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: { xs: 5, md: 7 } }}>
          <Logo size="medium" />
          <LanguageSwitcher />
        </Stack>

        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <Stack spacing={3} sx={{ maxWidth: 560 }}>
              <Chip
                label={t('publicHome.eyebrow',{ appName: APP_NAME })}
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: alpha(theme.palette.secondary.main, 0.12),
                  color: 'secondary.dark'
                }}
              />
              <Typography variant="h1" sx={{ fontSize: { xs: '2.7rem', md: '4.35rem' }, lineHeight: 1.02 }}>
                {t('publicHome.hero.title')}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 540 }}>
                {t('publicHome.hero.subtitle')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PersonAdd />}
                  onClick={() => openAuth('register')}
                  sx={{ px: 3, py: 1.5 }}
                >
                  <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                    <Box component="span">{t('publicHome.hero.primaryCta')}</Box>
                    <Box component="span" sx={{ fontSize: '0.74rem', fontWeight: 600, opacity: 0.82, mt: 0.25 }}>
                      {t('publicHome.hero.primaryCtaDetail')}
                    </Box>
                  </Box>
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<LoginIcon />}
                  onClick={() => openAuth('login')}
                  sx={{ px: 3, py: 1.5 }}
                >
                  {t('publicHome.hero.secondaryCta')}
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500 }}>
                {t('publicHome.hero.supportingCopy', { appName: APP_NAME })}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                position: 'relative',
                overflow: 'hidden',
                p: { xs: 2.5, sm: 3.5 },
                height: '100%',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: `linear-gradient(145deg, ${alpha('#fffaf5', 0.94)} 0%, ${alpha('#fff4ea', 0.88)} 100%)`,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -42,
                  right: -20,
                  width: 180,
                  height: 180,
                  borderRadius: '50%',
                  background: alpha(theme.palette.secondary.main, 0.12),
                  filter: 'blur(2px)',
                }}
              />
              <Stack spacing={2.5} sx={{ position: 'relative' }}>
                <Typography variant="overline" sx={{ color: 'secondary.dark', letterSpacing: '0.08em' }}>
                  {t('publicHome.preview.overline')}
                </Typography>
                <Typography variant="h5">
                  {t('publicHome.preview.title')}
                </Typography>
                <Typography color="text.secondary">
                  {t('publicHome.preview.description')}
                </Typography>

                <Stack spacing={1.5}>
                  <Paper sx={{ p: 2.25, bgcolor: alpha('#fffdf8', 0.86) }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Place sx={{ color: 'secondary.main', mt: 0.25 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {t('publicHome.preview.cards.houseGuide.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('publicHome.preview.cards.houseGuide.description')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  <Paper sx={{ p: 2.25, bgcolor: alpha('#fffdf8', 0.82) }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <TaskAlt sx={{ color: 'primary.main', mt: 0.25 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {t('publicHome.preview.cards.sharedPlanning.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('publicHome.preview.cards.sharedPlanning.description')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  <Paper
                    sx={{
                      p: 2.25,
                      color: 'common.white',
                      background: 'linear-gradient(135deg, #234436 0%, #426b58 100%)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: alpha('#fffdf8', 0.72), mb: 0.75 }}>
                      {t('publicHome.preview.tipLabel')}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 700 }}>
                      {t('publicHome.preview.tipTitle')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fffdf8', 0.82) }}>
                      {t('publicHome.preview.tipDescription')}
                    </Typography>
                  </Paper>
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: { xs: 4, md: 6 } }}>
          {featureCards.map((feature) => (
            <Grid item xs={12} md={4} key={feature.title}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                  backgroundColor: alpha('#fffaf5', 0.78),
                }}
              >
                <Stack spacing={2}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: 2.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6">{feature.title}</Typography>
                  <Typography color="text.secondary">{feature.description}</Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper
          sx={{
            mt: { xs: 4, md: 6 },
            p: { xs: 3, md: 4 },
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            background: `linear-gradient(135deg, ${alpha('#fff8f1', 0.92)} 0%, ${alpha('#f1e6d6', 0.92)} 100%)`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h4" sx={{ mb: 1 }}>
                {t('publicHome.bottomCta.title')}
              </Typography>
              <Typography color="text.secondary">
                {t('publicHome.bottomCta.description')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction={{ xs: 'column', sm: 'row', md: 'column' }} spacing={1.5}>
                <Button variant="contained" onClick={() => openAuth('register')} startIcon={<PersonAdd />}>
                  <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
                    <Box component="span">{t('publicHome.bottomCta.primaryCta')}</Box>
                    <Box component="span" sx={{ fontSize: '0.74rem', fontWeight: 600, opacity: 0.82, mt: 0.25 }}>
                      {t('publicHome.bottomCta.primaryCtaDetail')}
                    </Box>
                  </Box>
                </Button>
                <Button variant="text" onClick={() => openAuth('login')} startIcon={<LoginIcon />}>
                  {t('publicHome.bottomCta.secondaryCta')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Dialog
        open={Boolean(initialAuthMode)}
        onClose={closeAuth}
        fullWidth
        maxWidth={initialAuthMode === 'register' ? 'md' : 'sm'}
        fullScreen={fullScreenDialog}
      >
        <IconButton
          aria-label={t('publicHome.closeAuthDialog')}
          onClick={closeAuth}
          sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ px: { xs: 2, sm: 4 }, py: { xs: 6, sm: 5 } }}>
          {initialAuthMode === 'login' ? (
            <LoginForm redirectTo={redirectTo} initialEmail={initialEmail} showBranding={false} />
          ) : (
            <RegisterForm redirectTo={redirectTo} initialEmail={initialEmail} showBranding={false} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PublicHomePage;